import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';
import { formatXof } from '../utils/format';

const CONDITION = {
  NEUF: ['green', 'Neuf'], BON: ['blue', 'Bon'], USAGE: ['gold', 'Usagé'], HORS_SERVICE: ['red', 'Hors service'],
};
const STATUS = { EN_SERVICE: 'En service', SORTI: 'Sorti', REFORME: 'Réformé' };
const MOVES = { ENTREE: 'Entrée', AFFECTATION: 'Affectation', SORTIE: 'Sortie', REPARATION: 'Réparation', REFORME: 'Réforme' };

const EMPTY = { designation: '', category: '', brand: '', model: '', serialNumber: '', location: '', quantity: 1, condition: 'BON', acquisitionDate: '', unitValueXof: '', notes: '', unitMeasure: '', minThreshold: '', articleCode: '' };

export default function Assets() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [moveFor, setMoveFor] = useState(null);
  const [view, setView] = useState(null);

  function load() {
    api.get(`/assets${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`).then(setRows).catch((e) => setError(e.message));
    api.get('/assets/stats').then(setStats).catch(() => {});
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);

  async function archive(id) {
    if (!(await confirmDialog({ title: 'Archiver', message: 'Archiver ce matériel ?', confirmLabel: 'Archiver', danger: true }))) return;
    try { await api.del(`/assets/${id}`); load(); } catch (e) { setError(e.message); }
  }

  return (
    <>
      {stats && (
        <div className="kpi-grid">
          <div className="kpi navy"><span>Références</span><strong>{stats.count}</strong></div>
          <div className="kpi"><span>Quantité totale</span><strong>{stats.totalItems}</strong></div>
          <div className="kpi gold"><span>Valeur du parc</span><strong style={{ fontSize: '1.5rem' }}>{formatXof(stats.totalValue)}</strong></div>
          <div className={`kpi ${stats.outOfService ? '' : ''}`}><span>Hors service</span><strong>{stats.outOfService}</strong></div>
        </div>
      )}
      <div className="panel">
        <div className="panel-head">
          <h2>Inventaire du matériel</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="search-input" style={{ width: 220 }} placeholder="Désignation, marque, n° série…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="btn btn-sm" onClick={() => setModal({ ...EMPTY })}>+ Ajouter</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {rows.length === 0 ? <div className="empty">Aucun matériel.</div> : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Désignation</th><th>Catégorie</th><th>Emplacement</th><th className="num">Qté</th><th>État</th><th className="num">Val. unit.</th><th className="num">Total</th><th>Statut</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.map((a) => {
                    const [b, l] = CONDITION[a.condition] || ['gray', a.condition];
                    return (
                      <tr key={a.id}>
                        <td>{a.designation}{a.brand ? <><br /><small style={{ color: 'var(--muted)' }}>{a.brand} {a.model}</small></> : ''}</td>
                        <td>{a.category || '—'}</td>
                        <td>{a.location || '—'}</td>
                        <td className="num">{a.quantity}</td>
                        <td><span className={`badge ${b}`}>{l}</span></td>
                        <td className="num">{a.unitValueXof != null ? formatXof(a.unitValueXof) : '—'}</td>
                        <td className="num"><b>{a.unitValueXof != null ? formatXof(a.unitValueXof * a.quantity) : '—'}</b></td>
                        <td>{STATUS[a.status] || a.status}</td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn-ghost btn-sm" onClick={() => setView(a)}>Vue</button>
                          <button className="btn-ghost btn-sm" onClick={() => setMoveFor(a)}>Mouvement</button>
                          <button className="btn-ghost btn-sm" onClick={() => setModal(a)}>Modifier</button>
                          <button className="link-danger" onClick={() => archive(a.id)}>Archiver</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modal && <AssetModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {moveFor && <MovementModal asset={moveFor} onClose={() => setMoveFor(null)} onSaved={() => { setMoveFor(null); load(); }} />}
      {view && <AssetView id={view.id} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />}
    </>
  );
}

function AssetModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial, acquisitionDate: initial.acquisitionDate ? String(initial.acquisitionDate).slice(0, 10) : '', unitValueXof: initial.unitValueXof ?? '', quantity: initial.quantity ?? 1 });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    const payload = {
      designation: f.designation, category: f.category || undefined, brand: f.brand || undefined,
      model: f.model || undefined, serialNumber: f.serialNumber || undefined, location: f.location || undefined,
      quantity: Number(f.quantity) || 1, condition: f.condition,
      acquisitionDate: f.acquisitionDate || undefined,
      unitValueXof: f.unitValueXof !== '' ? Number(f.unitValueXof) : undefined,
      notes: f.notes || undefined,
      unitMeasure: f.unitMeasure || undefined,
      minThreshold: f.minThreshold !== '' ? Number(f.minThreshold) : undefined,
      articleCode: f.articleCode || undefined,
    };
    try {
      if (isEdit) await api.patch(`/assets/${initial.id}`, payload);
      else await api.post('/assets', payload);
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 16 }}>{isEdit ? 'Modifier le matériel' : 'Nouveau matériel'}</h2>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit}>
          <div className="grid-3">
            <div className="field" style={{ gridColumn: 'span 2' }}><label>Désignation *</label><input value={f.designation} onChange={set('designation')} required /></div>
            <div className="field"><label>Catégorie</label><select value={f.category} onChange={set('category')}><option value="">— Choisir —</option><option value="PED">Fournitures pédagogiques</option><option value="ADM">Registres administratifs</option><option value="HYG">Hygiène et santé</option><option value="ENT">Entretien</option><option value="ACT">Activités et éveil</option><option value="BUR">Bureau</option></select></div>
            <div className="field"><label>Marque</label><input value={f.brand} onChange={set('brand')} /></div>
            <div className="field"><label>Modèle</label><input value={f.model} onChange={set('model')} /></div>
            <div className="field"><label>N° de série</label><input value={f.serialNumber} onChange={set('serialNumber')} /></div>
            <div className="field"><label>Emplacement</label><input value={f.location} onChange={set('location')} /></div>
            <div className="field"><label>Quantité</label><input type="number" min="1" value={f.quantity} onChange={set('quantity')} /></div>
            <div className="field"><label>État</label>
              <select value={f.condition} onChange={set('condition')}>
                <option value="NEUF">Neuf</option><option value="BON">Bon</option><option value="USAGE">Usagé</option><option value="HORS_SERVICE">Hors service</option>
              </select></div>
            <div className="field"><label>Valeur unitaire (XOF)</label><input type="number" min="0" value={f.unitValueXof} onChange={set('unitValueXof')} /></div>
            <div className="field"><label>Date d'acquisition</label><input type="date" value={f.acquisitionDate} onChange={set('acquisitionDate')} /></div>
            <div className="field"><label>Unité de mesure</label><select value={f.unitMeasure} onChange={set('unitMeasure')}><option value="">— Choisir —</option><option value="unite">Unité</option><option value="paquet">Paquet</option><option value="rame">Rame</option><option value="flacon">Flacon</option><option value="bidon">Bidon</option></select></div>
            <div className="field"><label>Seuil minimum</label><input type="number" min="0" value={f.minThreshold} onChange={set('minThreshold')} placeholder="0" /></div>
            <div className="field"><label>Code article</label><input value={f.articleCode} onChange={set('articleCode')} placeholder="PED-CRA-BLA-001" /></div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer')}</button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MovementModal({ asset, onClose, onSaved }) {
  const [type, setType] = useState('AFFECTATION');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await api.post(`/assets/${asset.id}/movements`, { type, date, reason: reason || undefined, assignedTo: assignedTo || undefined });
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 4 }}>Mouvement de matériel</h2>
        <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginBottom: 16 }}>{asset.designation}</p>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit} className="form-grid">
          <div className="field"><label>Type de mouvement</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {Object.entries(MOVES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div className="field"><label>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div className="field"><label>Affecté à (personne / service)</label><input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} /></div>
          <div className="field"><label>Motif / observations</label><textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? '…' : 'Enregistrer'}</button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>

        {asset.movements && asset.movements.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="fieldset-title">Historique</div>
            {asset.movements.slice(0, 5).map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.84rem', padding: '4px 0', color: 'var(--muted)' }}>
                <span>{MOVES[m.type] || m.type}{m.assignedTo ? ` → ${m.assignedTo}` : ''}</span>
                <span className="mono">{new Date(m.date).toLocaleDateString('fr-FR')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function AssetView({ id, onClose, onEdit }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/assets/${id}`).then(setA).catch(() => {}); }, [id]);
  const R = (label, value) => (
    <div className="view-row"><span className="view-label">{label}</span><span className="view-value">{value != null && value !== '' ? value : '—'}</span></div>
  );
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        {!a ? <div className="empty">Chargement…</div> : (
          <>
            <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 4 }}>{a.designation}</h2>
            <div className="mono" style={{ color: 'var(--muted)', marginBottom: 12 }}>{a.category || '—'}{a.brand ? ` · ${a.brand} ${a.model || ''}` : ''}</div>
            <div className="view-grid">
              {R('N° de série', a.serialNumber)}{R('Emplacement', a.location)}
              {R('Quantité', a.quantity)}{R('État', (CONDITION[a.condition] || [])[1] || a.condition)}
              {R('Statut', STATUS[a.status] || a.status)}
              {R('Valeur unitaire', a.unitValueXof != null ? formatXof(a.unitValueXof) : null)}
              {R('Valeur totale', a.unitValueXof != null ? formatXof(a.unitValueXof * a.quantity) : null)}
              {R('Acquisition', a.acquisitionDate ? new Date(a.acquisitionDate).toLocaleDateString('fr-FR') : null)}
            </div>
            {a.notes && (<><div className="fieldset-title">Notes</div><div style={{ whiteSpace: 'pre-wrap' }}>{a.notes}</div></>)}
            {a.movements && a.movements.length > 0 && (
              <>
                <div className="fieldset-title">Historique des mouvements</div>
                {a.movements.map((m) => (
                  <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', padding: '4px 0', borderBottom: '1px dotted var(--line)' }}>
                    <span>{(MOVES[m.type] || m.type)}{m.assignedTo ? ` → ${m.assignedTo}` : ''}{m.reason ? ` (${m.reason})` : ''}</span>
                    <span className="mono" style={{ color: 'var(--muted)' }}>{new Date(m.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
              <button type="button" className="btn-ghost" onClick={onClose}>Fermer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
